import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Pages
import LoginPage from "@/pages/LoginPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import FeedPage from "@/pages/FeedPage";
import InspirationsPage from "@/pages/InspirationsPage";
import UploadPage from "@/pages/UploadPage";
import CentrumPage from "@/pages/CentrumPage";
import ChatPage from "@/pages/ChatPage";
import ReviewPage from "@/pages/ReviewPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminZombiebotPage from "@/pages/AdminZombiebotPage";

// Protected Route Component
const ProtectedRoute = ({ 
  children, 
  requireRoles = [], 
  allowPasswordChange = false,
  guestAllowed = false 
}) => {
  const auth = useAuth();
  const { user, loading, mustChangePassword } = auth;
  
  // Defensive check for hasRole function
  const hasRole = typeof auth.hasRole === 'function' ? auth.hasRole : (role) => {
    if (!user) return false;
    const roles = user.roles || [user.role];
    return roles.includes(role) || roles.includes('admin');
  };
  const isGuest = typeof auth.isGuest === 'function' ? auth.isGuest : () => false;

  if (loading) {
    return (
      <div className="h-screen w-full bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force password change if required
  if (mustChangePassword && !allowPasswordChange) {
    return <Navigate to="/change-password" replace />;
  }

  // Check role requirements
  if (requireRoles.length > 0) {
    const hasRequiredRole = requireRoles.some(role => hasRole(role));
    if (!hasRequiredRole) {
      return <Navigate to="/" replace />;
    }
  }

  // Check if guest is trying to access non-guest pages
  if (!guestAllowed && isGuest() && !hasRole('performer') && !hasRole('writer') && !hasRole('admin')) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main App Routes
const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen w-full bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      
      {/* Change Password */}
      <Route path="/change-password" element={
        <ProtectedRoute allowPasswordChange guestAllowed>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />
      
      {/* FEED - All users including guests */}
      <Route path="/" element={
        <ProtectedRoute guestAllowed>
          <FeedPage />
        </ProtectedRoute>
      } />
      
      {/* INSPIRATIONS - Performers+ */}
      <Route path="/inspirations" element={
        <ProtectedRoute requireRoles={['performer', 'writer', 'admin']}>
          <InspirationsPage />
        </ProtectedRoute>
      } />
      
      {/* UPLOAD - Performers+ */}
      <Route path="/upload" element={
        <ProtectedRoute requireRoles={['performer', 'writer', 'admin']}>
          <UploadPage />
        </ProtectedRoute>
      } />
      
      {/* CENTRUM (Character Center) - Performers+ */}
      <Route path="/centrum" element={
        <ProtectedRoute requireRoles={['performer', 'writer', 'admin']}>
          <CentrumPage />
        </ProtectedRoute>
      } />
      
      {/* FORUM - Performers+ */}
      <Route path="/forum" element={
        <ProtectedRoute requireRoles={['performer', 'writer', 'admin']}>
          <ChatPage />
        </ProtectedRoute>
      } />
      
      {/* Legacy chat redirect */}
      <Route path="/chat" element={<Navigate to="/forum" replace />} />
      
      {/* REVIEW - Writers and Admins */}
      <Route path="/review" element={
        <ProtectedRoute requireRoles={['writer', 'admin']}>
          <ReviewPage />
        </ProtectedRoute>
      } />
      
      {/* PROFILE */}
      <Route path="/profile" element={
        <ProtectedRoute guestAllowed>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/profile/:userId" element={
        <ProtectedRoute guestAllowed>
          <ProfilePage />
        </ProtectedRoute>
      } />
      
      {/* ADMIN Routes */}
      <Route path="/admin/users" element={
        <ProtectedRoute requireRoles={['admin']}>
          <AdminUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/zombiebot" element={
        <ProtectedRoute requireRoles={['admin']}>
          <AdminZombiebotPage />
        </ProtectedRoute>
      } />
      
      {/* Legacy redirects */}
      <Route path="/admin/review" element={<Navigate to="/review" replace />} />
      <Route path="/admin/assignments" element={<Navigate to="/" replace />} />
      <Route path="/my-submissions" element={<Navigate to="/centrum" replace />} />
      <Route path="/messages/*" element={<Navigate to="/forum" replace />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-black">
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
